import React from "react"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card"

/**
 * SetupTest Component
 *
 * This component is used to test the EDS (Evo Design System) setup.
 * It demonstrates the usage of EDS-themed shadcn/ui components:
 * - Button (with various variants)
 * - Input
 * - Card
 *
 * This is a dev-only component for validating the Tailwind CSS + shadcn/ui + EDS theme setup.
 */
export function SetupTest() {
  const [inputValue, setInputValue] = React.useState("")

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        <h1 className="text-3xl font-bold text-foreground">
          EDS Setup Test Page
        </h1>
        <p className="text-muted-foreground">
          This page tests the Tailwind CSS 4 + shadcn/ui + EDS theme setup.
        </p>

        {/* Button Variants */}
        <Card>
          <CardHeader>
            <CardTitle>Button Variants</CardTitle>
            <CardDescription>
              Testing different EDS button styles and variants.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-4">
              <Button>Default</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="destructive">Destructive</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="link">Link</Button>
            </div>
          </CardContent>
        </Card>

        {/* Button Sizes */}
        <Card>
          <CardHeader>
            <CardTitle>Button Sizes</CardTitle>
            <CardDescription>
              Testing different button sizes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-4">
              <Button size="sm">Small</Button>
              <Button size="default">Default</Button>
              <Button size="lg">Large</Button>
            </div>
          </CardContent>
        </Card>

        {/* Input Component */}
        <Card>
          <CardHeader>
            <CardTitle>Input Component</CardTitle>
            <CardDescription>
              Testing the EDS-themed input component.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-w-md">
              <div>
                <label
                  htmlFor="test-input"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  Test Input
                </label>
                <Input
                  id="test-input"
                  type="text"
                  placeholder="Type something..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="disabled-input"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  Disabled Input
                </label>
                <Input
                  id="disabled-input"
                  type="text"
                  placeholder="Disabled input"
                  disabled
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Card with Footer */}
        <Card>
          <CardHeader>
            <CardTitle>Card with Footer</CardTitle>
            <CardDescription>
              Testing card component with footer actions.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-foreground">
              This card demonstrates the full card layout with header, content,
              and footer sections. The footer contains action buttons.
            </p>
          </CardContent>
          <CardFooter className="flex justify-end gap-2">
            <Button variant="outline">Cancel</Button>
            <Button>Save</Button>
          </CardFooter>
        </Card>

        {/* Tailwind CSS Test */}
        <Card>
          <CardHeader>
            <CardTitle>Tailwind CSS Test</CardTitle>
            <CardDescription>
              Testing that Tailwind CSS classes apply correctly.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-primary text-primary-foreground rounded-md">
                Primary Background
              </div>
              <div className="p-4 bg-secondary text-secondary-foreground rounded-md">
                Secondary Background
              </div>
              <div className="p-4 bg-muted text-muted-foreground rounded-md">
                Muted Background
              </div>
              <div className="p-4 bg-accent text-accent-foreground rounded-md">
                Accent Background
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status */}
        <Card>
          <CardHeader>
            <CardTitle>Setup Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-foreground">
              <li>Tailwind CSS 4: Working</li>
              <li>shadcn/ui: Working</li>
              <li>EDS Theme: Working</li>
              <li>Button Component: Working</li>
              <li>Input Component: Working</li>
              <li>Card Component: Working</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default SetupTest
